import { FC, useState, useCallback, useLayoutEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import GridOnIcon from "@mui/icons-material/GridOn";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { CriterionPickerDialog } from "./CriterionPickerDialog";
import { SidebarSection, CriterionSidebarItem } from "./ScoringProfileSidebar";
import { CriterionEditorDialog } from "../CriterionEditorDialog";
import { CriterionReadOnlyView } from "../CriterionReadOnlyView";
import { useScoringStore, v1SourceId } from "@/store/scoringStore";
import { getLoggedUserProfile } from "@/utils/auth";
import {
  ScoringMethod,
  ScoringType,
  ScoringProfile,
  ScoringProfileCreate,
  CriterionKey,
} from "@/lib/scoring/types";
import {
  FIT_CRITERION_KEYS,
  INTENT_CRITERION_KEYS,
  CRITERION_LABELS,
  getCriterionKey,
  CRITERION_REGISTRY,
} from "@/lib/scoring/criterionRegistry";

export interface ScoringProfileDialogProps {
  open: boolean;
  profile: ScoringProfile | null;
  initialCriteria?: ScoringType[];
  initialName?: string;
  onClose: () => void;
  onSaved: () => void;
}

export const ScoringProfileDialog: FC<ScoringProfileDialogProps> = ({
  open,
  profile,
  initialCriteria,
  initialName,
  onClose,
  onSaved,
}) => {
  const allProfiles = useScoringStore((s) => s.profiles);
  const createProfile = useScoringStore((s) => s.createProfile);
  const updateProfile = useScoringStore((s) => s.updateProfile);
  const deleteProfile = useScoringStore((s) => s.deleteProfile);

  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const scoringMethod = ScoringMethod.WEIGHTED;
  const [criteria, setCriteria] = useState<ScoringType[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingCriterion, setEditingCriterion] = useState<{
    criterion: ScoringType;
    index: number | null;
  } | null>(null);
  const [localizeInstalls, setLocalizeInstalls] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null
  );

  const resetState = useCallback(() => {
    setName(profile?.name ?? initialName ?? "");
    setNameTouched(false);
    const newCriteria = profile?.parameters.criteria ?? initialCriteria ?? [];
    setCriteria(newCriteria);
    setSelectedIndex(
      initialCriteria && initialCriteria.length > 0
        ? null
        : newCriteria.length > 0
          ? 0
          : null
    );
    setLocalizeInstalls(profile?.parameters.localizeInstalls ?? true);
    setIsSaving(false);
    setSaveError(null);
    setShowDeleteConfirm(false);
    setShowDiscardConfirm(false);
    setPickerOpen(false);
    setEditingCriterion(null);
    setSettingsAnchor(null);
  }, [profile, initialCriteria, initialName]);

  useLayoutEffect(() => {
    if (open) resetState();
  }, [open, resetState]);

  const isDuplicate =
    nameTouched &&
    allProfiles.some(
      (p) =>
        p.name.trim().toLowerCase() === name.trim().toLowerCase() &&
        p.id !== profile?.id
    );
  const nameError =
    nameTouched && name.trim() === ""
      ? "Name is required"
      : isDuplicate
        ? "A scoring profile with this name already exists"
        : null;

  const fitCriteria = criteria.filter((c) => {
    const k = getCriterionKey(c);
    return k !== null && FIT_CRITERION_KEYS.includes(k as never);
  });
  const intentCriteria = criteria.filter((c) => {
    const k = getCriterionKey(c);
    return k !== null && INTENT_CRITERION_KEYS.includes(k as never);
  });

  const hasTechInstallCriteria = useMemo(
    () =>
      criteria.some((c) => {
        const k = getCriterionKey(c);
        return k !== null && CRITERION_REGISTRY[k]?.isTechInstall === true;
      }),
    [criteria]
  );

  const handlePickerSelect = (key: CriterionKey) => {
    const defaultName = CRITERION_LABELS[key];
    const existingNames = criteria.map((c) => c.name.trim());
    let name = defaultName;
    let count = 2;
    while (existingNames.includes(name)) {
      name = `${defaultName} (${count++})`;
    }
    setEditingCriterion({
      criterion: {
        name,
        [key]: (() => {
          const b = CRITERION_REGISTRY[key]?.initialBand;
          if (!b) return [];
          return Array.isArray(b) ? b : [b];
        })(),
      },
      index: null,
    });
  };

  const handleEditCriterion = (index: number) => {
    setEditingCriterion({ criterion: criteria[index], index });
  };

  const handleEditorConfirm = (updated: ScoringType) => {
    if (!editingCriterion) return;
    if (editingCriterion.index === null) {
      // New criterion — auto-fill weight from max band score
      const key = getCriterionKey(updated);
      if (key && updated.weight === undefined) {
        const bands = updated[key] as Array<{ score: number }> | undefined;
        if (bands?.length) {
          updated.weight = Math.max(...bands.map((b) => b.score));
        }
      }
      const newIndex = criteria.length;
      setCriteria((prev) => [...prev, updated]);
      setSelectedIndex(newIndex);
    } else {
      setCriteria((prev) =>
        prev.map((c, i) => (i === editingCriterion.index ? updated : c))
      );
    }
    setEditingCriterion(null);
  };

  const handleCriterionChange = (index: number, updated: ScoringType) => {
    setCriteria((prev) => prev.map((c, i) => (i === index ? updated : c)));
  };

  const handleCriterionDelete = (index: number) => {
    const newLength = criteria.length - 1;
    setCriteria((prev) => prev.filter((_, i) => i !== index));
    setSelectedIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) {
        if (newLength === 0) return null;
        return Math.min(prev, newLength - 1);
      }
      return prev > index ? prev - 1 : prev;
    });
  };

  const handleSave = async () => {
    setNameTouched(true);
    const isNameEmpty = !name.trim();
    const isNameDuplicate = allProfiles.some(
      (p) =>
        p.name.trim().toLowerCase() === name.trim().toLowerCase() &&
        p.id !== profile?.id
    );
    if (isNameEmpty || isNameDuplicate) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const email = getLoggedUserProfile()?.email ?? "";
      const parameters = {
        criteria,
        scoringMethod,
        localizeInstalls,
        version: 1,
      };
      if (profile) {
        await updateProfile({ ...profile, name: name.trim(), parameters });
      } else {
        const payload: ScoringProfileCreate = {
          name: name.trim(),
          email,
          spog: true,
          parameters,
        };
        const created = await createProfile(payload);
        // Reset active sources to only the newly created one (matches PIQ behavior).
        // Must go through the action, not setState — `activeSourceIds` is canonical
        // and `activeProfileIds` is derived from it, so writing the mirror directly
        // leaves the V2 source picker showing a stale selection.
        useScoringStore.getState().setActiveSourceIds([v1SourceId(created.id)]);
      }
      onSaved();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save profile"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!profile) return;
    setIsSaving(true);
    try {
      await deleteProfile(profile.id);
      onSaved();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to delete profile"
      );
      setIsSaving(false);
    }
  };

  const hasUnsavedChanges = (() => {
    const originalName = profile?.name ?? initialName ?? "";
    const originalCriteria =
      profile?.parameters.criteria ?? initialCriteria ?? [];
    if (name.trim() !== originalName.trim()) return true;
    if (criteria.length !== originalCriteria.length) return true;
    if (JSON.stringify(criteria) !== JSON.stringify(originalCriteria))
      return true;
    if (localizeInstalls !== (profile?.parameters.localizeInstalls ?? true))
      return true;
    return false;
  })();

  const handleCloseAttempt = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const isCreate = profile === null && !initialCriteria;
  const hasCriteriaErrors = criteria.some((c) => {
    const key = getCriterionKey(c);
    if (!key) return false;
    const entry = CRITERION_REGISTRY[key];
    if (!entry?.hasErrors) return false;
    return entry.hasErrors((c[key] as unknown[]) ?? []);
  });
  const isSaveDisabled =
    isSaving ||
    (nameTouched && !!nameError) ||
    criteria.length === 0 ||
    hasCriteriaErrors;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseAttempt}
        maxWidth="md"
        fullWidth
        TransitionProps={{ onExited: resetState }}
      >
        <DialogTitle>
          {isCreate ? "New Scoring Profile" : "Edit Scoring Profile"}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ p: 0, display: "flex", minHeight: 500, overflow: "hidden" }}
        >
          {/* LEFT SIDEBAR */}
          <Box
            sx={{
              width: 260,
              borderRight: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <TextField
                label="Profile name"
                required
                variant="outlined"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!nameError}
                helperText={nameError ?? " "}
                size="small"
                slotProps={{
                  htmlInput: { "data-testid": "scoring-profile-name-input" },
                }}
              />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto" }}>
              <SidebarSection
                label="ACCOUNT FIT SCORES"
                icon={<GridOnIcon fontSize="small" />}
                empty={fitCriteria.length === 0}
              >
                {fitCriteria.map((c) => {
                  const globalIndex = criteria.indexOf(c);
                  return (
                    <CriterionSidebarItem
                      key={globalIndex}
                      label={c.name || CRITERION_LABELS[getCriterionKey(c)!]}
                      selected={selectedIndex === globalIndex}
                      onSelect={() => setSelectedIndex(globalIndex)}
                      onDelete={() => handleCriterionDelete(globalIndex)}
                      wasGenerated={c.wasGenerated}
                      weight={c.weight}
                    />
                  );
                })}
              </SidebarSection>

              <SidebarSection
                label="INTENT SCORES"
                icon={<ManageSearchIcon fontSize="small" />}
                empty={intentCriteria.length === 0}
              >
                {intentCriteria.map((c) => {
                  const globalIndex = criteria.indexOf(c);
                  return (
                    <CriterionSidebarItem
                      key={globalIndex}
                      label={c.name || CRITERION_LABELS[getCriterionKey(c)!]}
                      selected={selectedIndex === globalIndex}
                      onSelect={() => setSelectedIndex(globalIndex)}
                      onDelete={() => handleCriterionDelete(globalIndex)}
                      wasGenerated={c.wasGenerated}
                      weight={c.weight}
                    />
                  );
                })}
              </SidebarSection>
            </Box>

            {hasTechInstallCriteria && (
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  bgcolor: "grey.100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <SettingsIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, letterSpacing: 0.5 }}
                  >
                    TECH INSTALLS SETTINGS
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => setSettingsAnchor(e.currentTarget)}
                  aria-label="Open tech installs settings"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Box
              sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}
            >
              <Button
                fullWidth
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setPickerOpen(true)}
                sx={{ fontWeight: 700, letterSpacing: 0.5 }}
                data-testid="scoring-profile-add-score-btn"
              >
                ADD A SCORE
              </Button>
            </Box>
          </Box>

          {/* RIGHT PANE */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
            {saveError && (
              <Alert
                severity="error"
                onClose={() => setSaveError(null)}
                sx={{ mb: 2 }}
              >
                {saveError}
              </Alert>
            )}
            {selectedIndex === null ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  color: "text.secondary",
                  px: 4,
                }}
              >
                {initialCriteria && initialCriteria.length > 0 ? (
                  <>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      color="text.primary"
                    >
                      Complete!
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your Automated Scoring Profile has been generated
                    </Typography>
                    <Box
                      sx={{
                        mt: 3,
                        textAlign: "left",
                        maxWidth: 480,
                        display: "flex",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          bgcolor: "success.light",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          alignSelf: "center",
                        }}
                      >
                        <ArrowBackIcon
                          sx={{ color: "success.dark", fontSize: 32 }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Select any of the scoring types to the left to
                          discover, review, and edit the criteria generated from
                          the analysis
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Use the &quot;Add A Score&quot; button to add more
                          scoring types to this scoring profile
                        </Typography>
                        <Typography variant="body2">
                          Select &quot;Create&quot; to prioritize your account
                          list based on the criteria in this scoring profile
                        </Typography>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography variant="body2">
                      To begin, add a scoring type.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setPickerOpen(true)}
                      sx={{ fontWeight: 700, letterSpacing: 0.5 }}
                    >
                      ADD A SCORE
                    </Button>
                  </>
                )}
              </Box>
            ) : (
              <CriterionReadOnlyView
                key={selectedIndex}
                criterion={criteria[selectedIndex]}
                onEdit={() => handleEditCriterion(selectedIndex)}
                onDelete={() => handleCriterionDelete(selectedIndex)}
                onWeightChange={(weight) =>
                  handleCriterionChange(selectedIndex, {
                    ...criteria[selectedIndex],
                    weight,
                  })
                }
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Box>
            {profile && (
              <Button
                color="error"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSaving}
                data-testid="scoring-delete-this-profile-btn"
              >
                Delete This Profile
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleCloseAttempt} color="inherit">
              CANCEL
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaveDisabled}
              startIcon={isSaving ? <CircularProgress size={14} /> : undefined}
              data-testid="scoring-profile-save-btn"
            >
              {isCreate ? "CREATE PROFILE" : "SAVE"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <CriterionPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(key) => {
          setPickerOpen(false);
          handlePickerSelect(key);
        }}
      />

      <CriterionEditorDialog
        open={editingCriterion !== null}
        criterion={editingCriterion?.criterion ?? null}
        isNew={editingCriterion?.index === null}
        onConfirm={handleEditorConfirm}
        onCancel={() => setEditingCriterion(null)}
      />

      <Dialog
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDiscardConfirm(false);
              onClose();
            }}
            color="error"
          >
            Discard
          </Button>
          <Button onClick={() => setShowDiscardConfirm(false)} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete scoring profile?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            &quot;{profile?.name}&quot; will be permanently deleted and
            deactivated from the current session.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            color="inherit"
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            color="error"
            onClick={handleDelete}
            disabled={isSaving}
            data-testid="scoring-delete-confirm-btn"
          >
            {isSaving ? <CircularProgress size={14} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={hasTechInstallCriteria && Boolean(settingsAnchor)}
        anchorEl={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Install Localization
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Disabling this option will score companies if an install is detected
            anywhere in the company&apos;s global footprint, even outside the
            country of the company
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={localizeInstalls}
                onChange={(e) => setLocalizeInstalls(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Only return companies if the install data is detected in the
                same country as the company (Recommended)
              </Typography>
            }
          />
        </Box>
      </Popover>
    </>
  );
};
